// s12b Добавить/редактировать визит (Mini-sprint 6-visits Stage 4 §6).
// Один экран обслуживает create и edit. Дата + диагноз обязательны.

import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { useLocation, useRoute } from 'wouter';
import { ArrowLeft, Camera, Paperclip, Upload, X } from 'lucide-react';

import { MobileFrame } from '@/components/layout/MobileFrame';
import { applyValidationErrors, getApiMessage, getValidationErrors } from '@/api/error';
import { useToast } from '@/hooks/use-toast';
import { MaskedDateInput, isRealIsoDate } from '@/components/forms/MaskedDateInput';
import {
  useVisit,
  useCreateVisitMutation,
  useUpdateVisitMutation,
  useDeleteVisitAttachmentMutation,
  uploadVisitAttachmentDirect,
} from '@/api/hooks/visits';
import { queryKeys } from '@/api/queryKeys';
import { formatFileSize } from '@/lib/visit-format';

const schema = z.object({
  visit_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Укажите дату')
    .refine(isRealIsoDate, 'Такой даты не существует'),
  doctor_name: z.string().max(200).optional(),
  clinic_name: z.string().max(200).optional(),
  diagnosis: z.string().min(1, 'Укажите диагноз').max(500),
  notes: z.string().max(5000).optional(),
});

type FormValues = z.infer<typeof schema>;

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILES = 10;

/**
 * HEAL-67: у вложения 422 приходит на поле `file` — обычное сообщение из
 * `data.message` в этом случае служебное («The given data was invalid»), поэтому
 * сначала достаём текст самой ошибки поля.
 */
function getAttachmentReason(err: unknown): string | null {
  const fieldMessage = getValidationErrors(err)?.file?.[0];
  if (fieldMessage) return fieldMessage;
  return getApiMessage(err, '') || null;
}

export function VisitForm() {
  const [matchEdit, paramsEdit] = useRoute('/visits/:id/edit');
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();

  const mode = matchEdit ? 'edit' : 'create';
  const editId = paramsEdit?.id ? Number(paramsEdit.id) : undefined;

  const { data: existingVisit } = useVisit(mode === 'edit' ? editId : undefined);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { visit_date: '' }, // дата пустая по умолчанию (PM — без «сегодня»)
  });

  useEffect(() => {
    if (mode === 'edit' && existingVisit) {
      form.reset({
        visit_date: existingVisit.visit_date,
        doctor_name: existingVisit.doctor_name ?? '',
        clinic_name: existingVisit.clinic_name ?? '',
        diagnosis: existingVisit.diagnosis,
        notes: existingVisit.notes ?? '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, existingVisit]);

  const createMut = useCreateVisitMutation();
  const updateMut = useUpdateVisitMutation(editId ?? 0);

  // Единый флаг отправки на ВЕСЬ submit (create-визит + последовательная загрузка
  // файлов), чтобы кнопка «Сохранить» оставалась disabled до конца — иначе после
  // POST визита кнопка снова активна, пока файлы грузятся → повторный тап = дубль визита.
  const [submitting, setSubmitting] = useState(false);

  // Вложения. create: список local-файлов до сохранения. edit: реальные attachments
  // визита, новые аплоадятся сразу. Схема/бэкенд поддерживают много файлов на визит.
  const [localFiles, setLocalFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const existingAttachments = mode === 'edit' ? (existingVisit?.attachments ?? []) : [];
  const deleteAttMut = useDeleteVisitAttachmentMutation(editId ?? 0);
  const busy = uploading || deleteAttMut.isPending;

  const currentCount = mode === 'create' ? localFiles.length : existingAttachments.length;

  // Общий обработчик выбора (и из файлов, и с камеры) — поддерживает несколько файлов.
  function handleFiles(picked: File[]) {
    // Во время submit'а (create аплоадит snapshot localFiles) менять список нельзя —
    // добавленный файл silently не уйдёт, удалённый всё равно загрузится.
    if (submitting) return;
    const valid: File[] = [];
    const oversize: string[] = [];
    for (const f of picked) {
      if (f.size > MAX_FILE_BYTES) oversize.push(f.name);
      else valid.push(f);
    }
    if (oversize.length) {
      toast({
        title: 'Файл больше 10 МБ',
        description: `Пропущены: ${oversize.join(', ')}. Сожмите или прикрепите меньший.`,
        variant: 'warning',
      });
    }
    if (!valid.length) return;

    const room = MAX_FILES - currentCount;
    if (room <= 0) {
      toast({ title: 'Лимит файлов', description: `Не больше ${MAX_FILES} файлов на визит.`, variant: 'warning' });
      return;
    }
    const toAdd = valid.slice(0, room);
    if (toAdd.length < valid.length) {
      toast({ title: 'Лимит файлов', description: `Добавлены не все — максимум ${MAX_FILES}.`, variant: 'warning' });
    }

    if (mode === 'create') {
      setLocalFiles((prev) => [...prev, ...toAdd]);
    } else if (editId) {
      void uploadFilesEdit(editId, toAdd);
    }
  }

  // edit: аплоад нескольких файлов подряд + один invalidate в конце.
  async function uploadFilesEdit(visitId: number, files: File[]) {
    setUploading(true);
    const failed: string[] = [];
    // HEAL-67: причину отказа по вложению сервер называет («Тип файла не
    // соответствует расширению», «Файл больше 10 МБ»), а мы показывали только
    // список имён. Берём сообщение первого отказа — оно почти всегда общее для пачки.
    let reason: string | null = null;
    for (const f of files) {
      try {
        await uploadVisitAttachmentDirect(visitId, f);
      } catch (err) {
        failed.push(f.name);
        reason ??= getAttachmentReason(err);
      }
    }
    await qc.invalidateQueries({ queryKey: queryKeys.visit(visitId) });
    setUploading(false);
    if (failed.length) {
      toast({
        title: 'Часть файлов не загрузилась',
        description: `Не удалось: ${failed.join(', ')}.${reason ? ` ${reason}` : ''}`,
        variant: 'warning',
      });
    }
  }

  function removeLocalFile(index: number) {
    if (submitting) return; // нельзя менять список во время загрузки snapshot'а
    setLocalFiles((prev) => prev.filter((_, i) => i !== index));
  }

  function removeExistingAttachment(attachmentId: number) {
    deleteAttMut.mutate(attachmentId, {
      onError: () =>
        toast({
          title: 'Не удалось удалить файл',
          description: 'Проверьте соединение и попробуйте ещё раз.',
          variant: 'destructive',
        }),
    });
  }

  async function onSubmit(values: FormValues) {
    if (submitting) return; // re-entry guard (двойной submit = дубль визита)
    setSubmitting(true);
    try {
      if (mode === 'create') {
        const created = await createMut.mutateAsync(values);
        const failed: string[] = [];
        let reason: string | null = null;
        for (const f of localFiles) {
          try {
            await uploadVisitAttachmentDirect(created.id, f);
          } catch (err) {
            failed.push(f.name);
            reason ??= getAttachmentReason(err); // HEAL-67: причина, а не только имя
          }
        }
        if (failed.length) {
          toast({
            title: 'Часть файлов не загрузилась',
            description:
              `Не удалось: ${failed.join(', ')}.` +
              `${reason ? ` ${reason}` : ''} Можно добавить их в режиме редактирования.`,
            variant: 'warning',
          });
        }
        setLocation(`/visits/${created.id}`);
      } else if (mode === 'edit' && editId) {
        await updateMut.mutateAsync(values);
        setLocation(`/visits/${editId}`);
      }
    } catch (err) {
      // HEAL-67: раньше здесь стоял `catch {}` с одним общим тостом «Не удалось
      // сохранить. Проверьте соединение» — он глотал ответ сервера целиком. При 422
      // (не влезло имя файла, невалидная дата, слишком длинный диагноз) пользователь
      // видел «проверьте соединение» и не понимал, что чинить; мы по багрепорту — тоже.
      // Теперь ошибки полей подсвечиваются на самих полях, а серверное сообщение
      // показывается как есть; «проверьте соединение» осталось только там, где это
      // правда — когда ответа не было вовсе.
      const applied = applyValidationErrors<keyof FormValues>(err, form.setError);
      if (!applied) {
        toast({
          title: 'Не удалось сохранить',
          description: getApiMessage(err, 'Проверьте соединение и попробуйте ещё раз.'),
          variant: 'destructive',
        });
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Единый список для отрисовки (local-файлы в create / attachments в edit).
  const attachmentItems =
    mode === 'create'
      ? localFiles.map((f, i) => ({
          key: `local-${i}`,
          name: f.name,
          size: f.size,
          mime: f.type,
          onRemove: () => removeLocalFile(i),
        }))
      : existingAttachments.map((a) => ({
          key: `att-${a.id}`,
          name: a.original_filename,
          size: a.size_bytes,
          mime: a.mime_type,
          onRemove: () => removeExistingAttachment(a.id),
        }));

  return (
    <MobileFrame>
      <div className="flex flex-shrink-0 items-center gap-2 border-b border-border px-3 pb-3 pt-2">
        <button
          type="button"
          onClick={() => setLocation(mode === 'edit' ? `/visits/${editId}` : '/visits')}
          className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted"
          aria-label="Назад"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1 text-base font-semibold">
          {mode === 'create' ? 'Новый визит' : 'Визит'}
        </div>
        <button
          type="submit"
          form="visit-form"
          disabled={submitting}
          className="h-8 rounded-control bg-primary px-3 text-xs font-semibold text-primary-foreground disabled:opacity-50"
        >
          {submitting ? 'Сохранение…' : 'Сохранить'}
        </button>
      </div>

      <form
        id="visit-form"
        onSubmit={form.handleSubmit(onSubmit)}
        className="flex-1 space-y-4 overflow-y-auto px-5 py-5"
      >
        <Field label="Дата визита" required error={form.formState.errors.visit_date?.message}>
          <Controller
            control={form.control}
            name="visit_date"
            render={({ field }) => (
              <MaskedDateInput
                value={field.value ?? ''}
                onChange={field.onChange}
                onBlur={field.onBlur}
                className="h-10 w-full rounded-control border border-gray-300 px-3 text-sm"
              />
            )}
          />
        </Field>

        {/* HEAL-74: в подсказке был пример «Иванов И.И.». Выдуманное ФИО в
            медицинском приложении читается как чужая запись, а не как подсказка. */}
        <Field label="Врач">
          <input
            type="text"
            placeholder="Введите ФИО врача"
            {...form.register('doctor_name')}
            className="h-10 w-full rounded-control border border-gray-300 px-3 text-sm"
          />
        </Field>

        <Field label="Клиника">
          <input
            type="text"
            placeholder="Медицинский центр"
            {...form.register('clinic_name')}
            className="h-10 w-full rounded-control border border-gray-300 px-3 text-sm"
          />
        </Field>

        <Field label="Диагноз / причина визита" required error={form.formState.errors.diagnosis?.message}>
          <input
            type="text"
            placeholder="Диагноз или причина визита"
            {...form.register('diagnosis')}
            className="h-10 w-full rounded-control border border-gray-300 px-3 text-sm"
          />
        </Field>

        <Field label="Комментарии" hint="Назначения, дозировки и расписание — здесь свободной формой.">
          <textarea
            rows={5}
            placeholder="Что обсудили, назначения, рекомендации, ощущения"
            {...form.register('notes')}
            className="w-full resize-none rounded-control border border-gray-300 px-3 py-2 text-sm"
          />
        </Field>

        <AttachmentBlock
          items={attachmentItems}
          busy={busy || submitting}
          onPickFile={() => fileInputRef.current?.click()}
          onPickCamera={() => cameraInputRef.current?.click()}
        />
        {/* Файлы: PDF + фото, можно выбрать несколько сразу.
            HEAL-67: image/heif — второе имя того же формата с iOS, см. бэкенд-валидацию. */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="application/pdf,image/jpeg,image/png,image/heic,image/heif"
          className="hidden"
          aria-label="Прикрепить файлы"
          onChange={(e) => {
            const fs = Array.from(e.target.files ?? []);
            if (fs.length) handleFiles(fs);
            e.target.value = '';
          }}
        />
        {/* Камера: на телефоне открывает съёмку сразу (capture) */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          className="hidden"
          aria-label="Сделать фото"
          onChange={(e) => {
            const fs = Array.from(e.target.files ?? []);
            if (fs.length) handleFiles(fs);
            e.target.value = '';
          }}
        />

        {/* Disclaimer */}
        <div className="rounded-surface border border-amber-200 bg-amber-50 p-3 text-hl-small leading-snug text-amber-900">
          Это просто запись для вас. Мы <strong>не напоминаем</strong> о приёме лекарств и не присылаем
          уведомления о визите.
        </div>
      </form>
    </MobileFrame>
  );
}

function Field({
  label,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div>
      <label className="mb-1.5 block text-hl-small font-semibold text-gray-700">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-hl-small text-muted-foreground">{hint}</p>}
      {error && <p className="mt-1 text-hl-small text-red-600">{error}</p>}
    </div>
  );
}

type AttachmentItem = {
  key: string;
  name: string;
  size: number;
  mime: string;
  onRemove: () => void;
};

function AttachmentBlock({
  items,
  busy,
  onPickFile,
  onPickCamera,
}: {
  items: AttachmentItem[];
  busy: boolean;
  onPickFile: () => void;
  onPickCamera: () => void;
}) {
  const hasItems = items.length > 0;

  return (
    <div>
      <label className="mb-1.5 block text-hl-small font-semibold text-gray-700">
        Заключения и фото
      </label>

      {/* Список уже выбранных/загруженных файлов */}
      {hasItems && (
        <div className="mb-2 space-y-2">
          {items.map((it) => (
            <div
              key={it.key}
              className="flex items-center gap-3 rounded-surface border border-border bg-surface p-3"
            >
              <FileIconSmall mime={it.mime} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{it.name}</div>
                <div className="text-hl-small text-muted-foreground">{formatFileSize(it.size)}</div>
              </div>
              <button
                type="button"
                onClick={it.onRemove}
                disabled={busy}
                className="h-7 w-7 text-gray-400 hover:text-red-500 disabled:opacity-50"
                aria-label="Удалить файл"
              >
                <X className="mx-auto h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Блок добавления — всегда виден, чтобы можно было прикрепить ещё */}
      <div className="flex flex-col items-center justify-center rounded-surface border-2 border-dashed border-gray-300 bg-muted p-4 text-center">
        {!hasItems ? (
          <>
            <div className="mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary-text">
              <Upload className="h-5 w-5" />
            </div>
            <div className="mb-0.5 text-hl-secondary font-semibold">
              Прикрепите файл или сделайте фото
            </div>
            <div className="mb-3 text-hl-small text-muted-foreground">
              PDF или фото: заключение врача, рецепт, выписка. До 10 МБ, можно несколько.
            </div>
          </>
        ) : (
          <div className="mb-3 text-hl-small text-muted-foreground">Можно добавить ещё файл или фото</div>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onPickFile}
            disabled={busy}
            className="flex h-8 items-center gap-1.5 rounded-control border border-gray-300 bg-surface px-3 text-hl-small font-medium disabled:opacity-50"
          >
            <Paperclip className="h-3.5 w-3.5" /> Файл
          </button>
          <button
            type="button"
            onClick={onPickCamera}
            disabled={busy}
            className="flex h-8 items-center gap-1.5 rounded-control border border-gray-300 bg-surface px-3 text-hl-small font-medium disabled:opacity-50"
          >
            <Camera className="h-3.5 w-3.5" /> Камера
          </button>
        </div>
        {busy && <div className="mt-2 text-hl-small text-muted-foreground">Загрузка…</div>}
      </div>
    </div>
  );
}

function FileIconSmall({ mime }: { mime: string }) {
  const isPdf = mime === 'application/pdf';
  return (
    <div
      className={`flex h-12 w-10 flex-shrink-0 items-center justify-center rounded-md ${isPdf ? 'bg-red-100' : 'bg-blue-100'}`}
    >
      <span className={`text-hl-small font-bold ${isPdf ? 'text-red-700' : 'text-blue-700'}`}>
        {isPdf ? 'PDF' : 'IMG'}
      </span>
    </div>
  );
}
